import React from 'react';
import { Link } from 'react-router-dom';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { CatalogTable } from '../../components/page/CatalogTable';
import { TechAssistCallout } from '../../components/page/TechAssistCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
import { catalog, insertFamilies } from '../../data/catalog';
function StepHeading({
  n,
  title
}: {
  n: number;
  title: string;
}) {
  return <div className="flex items-baseline gap-4" data-id="element-874" data-mp="wavsnjzvy5al">
      <span className="rp-tabular font-display text-[40px] font-medium leading-none text-navy/20" data-id="element-875" data-mp="wa2o8poefrvm">
        {`0${n}`}
      </span>
      <h2 className="font-display text-[clamp(1.35rem,2.2vw,1.8rem)] font-medium text-navy" data-id="element-876" data-mp="117cged36ahn5">
        {title}
      </h2>
    </div>;
}
export function ToolSelectionPage() {
  usePageMeta({
    title: 'Tool Selection: Holder, Insert & Application | Robertson Precision',
    description: 'Choose a Shear Geometry setup in three steps: match the SALXR holder shank to your turret, select APGX grade, geometry and radius, then review the application.',
    canonical: canonicalFor('/technical-resources/tool-selection/')
  });
  return <>
      <PageHeader eyebrow="Technical Resources" title="Tool Selection" lead="Three steps from machine to first cut. The options below are the catalog choices — not a recommendation for a specific job." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Technical Resources',
      href: '/technical-resources/'
    }, {
      label: 'Tool Selection'
    }]} data-id="element-877" data-mp="i8kin89cvli1" />

      <section aria-label="Step 1" className="w-full border-b border-rule bg-white" data-id="element-878" data-mp="11y4oc9u5cdlu">
        <div className="mx-auto max-w-shell px-5 py-12 lg:px-10 lg:py-16" data-id="element-879" data-mp="37r33ech9kloe">
          <StepHeading n={1} title="Match the holder shank to your turret" data-id="element-880" data-mp="33pht8yw46lhw" />
          <p className="rp-measure mt-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-881" data-mp="16obvmej1491c">
            Choose the holder that fits the tool station on your turret. A{' '}
            <Link to="/products/starter-kits/" className="text-navy underline decoration-copper underline-offset-4 hover:text-copper" data-id="element-882" data-mp="1bp9e6s7yb53d">
              starter kit
            </Link>{' '}
            pairs either size with inserts, a driver and extra screws.
          </p>
          <div className="mt-8" data-id="element-883" data-mp="1pl3nhccmzwh0">
            <CatalogTable rows={catalog.filter(c => c.category === 'holders' || c.category === 'kits')} caption="Holder and starter kit options" data-id="element-884" data-mp="1ulj36ake5g25" />
          </div>
        </div>
      </section>

      <section aria-label="Step 2" className="w-full border-b border-rule bg-warm" data-id="element-885" data-mp="24bay55wekb75">
        <div className="mx-auto max-w-shell px-5 py-12 lg:px-10 lg:py-16" data-id="element-886" data-mp="29bypjsojmu1w">
          <StepHeading n={2} title="Select insert grade, geometry and radius" data-id="element-887" data-mp="2dc0r81ua3nh6" />
          <p className="rp-measure mt-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-888" data-mp="1p6zqgyll5wn">
            Choose the family for the material and chip-control goal, then the
            nose radius. MG12 and DR80 are different grades even where part
            numbers overlap.
          </p>
          <div className="mt-8 space-y-10" data-id="element-889" data-mp="3ovpt02m1g4jo">
            {insertFamilies.map(family => <div key={family.id} data-id="element-890" data-mp="3jwwh2emegllm">
                <h3 className="font-display text-[18px] font-medium text-navy" data-id="element-891" data-mp="ldo22b4vq33n">
                  {family.title}
                </h3>
                <p className="mt-1.5 text-[14px] text-graphite/80" data-id="element-892" data-mp="gfeaeaid5kms">{family.use}</p>
                <div className="mt-4" data-id="element-893" data-mp="bmbbrnyqbkzq">
                  <CatalogTable rows={catalog.filter(c => c.family === family.id)} caption={family.title} variant="inserts" data-id="element-894" data-mp="bl9xzbqbwkeb" />
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <section aria-label="Step 3" className="w-full border-b border-rule bg-white" data-id="element-895" data-mp="zaun6cd2sdw1">
        <div className="mx-auto max-w-shell px-5 py-12 lg:px-10 lg:py-16" data-id="element-896" data-mp="jtmixi5yz5hr">
          <StepHeading n={3} title="Discuss the application and workholding before the first cut" data-id="element-897" data-mp="nxaro46c874l" />
          <p className="rp-measure mt-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-898" data-mp="9y1l7w7xordt">
            Review the{' '}
            <Link to="/technical-resources/starting-data/" className="text-navy underline decoration-copper underline-offset-4 hover:text-copper" data-id="element-899" data-mp="ew35gkh3iwqv">
              published starting data
            </Link>{' '}
            and{' '}
            <Link to="/technical-resources/safety/" className="text-navy underline decoration-copper underline-offset-4 hover:text-copper" data-id="element-900" data-mp="sukpo87slr41">
              workholding guidance
            </Link>
            , then talk the setup through with the technical staff.
          </p>
          <div className="mt-8" data-id="element-901" data-mp="1qckqlof8tcy7">
            <TechAssistCallout location="tool-selection" showPrepList data-id="element-902" data-mp="1qct67o2r2wd3" />
          </div>
        </div>
      </section>

      <RelatedLinks links={[{
      label: 'Products',
      href: '/products/',
      description: 'The full 13-part catalog.'
    }, {
      label: 'Applications',
      href: '/applications/',
      description: 'Guidance by material.'
    }, {
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'Published values in one table.'
    }, {
      label: 'Workholding & Safety',
      href: '/technical-resources/safety/',
      description: 'Setup checks and chart.'
    }]} data-id="element-903" data-mp="362k8oau9hb5r" />
    </>;
}